"use client";

import Script from "next/script";
import { useEffect, useRef } from "react";
import { useBotpressWebchat } from "./use-botpress-webchat";

/**
 * Determines the page section based on the current pathname
 */

/**
 * Webchat content component - Uses Next.js Script for simple, robust loading
 */
export function BotpressScripts() {
	const {
		isFirstScriptInjected,
		setIsFirstScriptInjected,
		setIsSecondScriptLoaded,
		isSecondScriptLoaded,
		isInitialized,
		setIsInitialized,
		setIsOpen,
		isOpen,
		getUserData,
	} = useBotpressWebchat();

	// Track previous state to avoid unnecessary re-renders
	const previousStateRef = useRef(isOpen);

	// Listen for Botpress initialization event
	useEffect(() => {
		if (!isSecondScriptLoaded || !window.botpress) return;

		const handleInit = () => {
			setIsInitialized(true);
		};

		window.botpress.on("webchat:initialized", handleInit);
	}, [isSecondScriptLoaded, setIsInitialized]);

	// Poll DOM to detect chat open/close state since events don't fire
	useEffect(() => {
		if (!isInitialized || !window.botpress) return;

		// Function to check if chat is currently open by looking at Shadow DOM
		const checkChatState = () => {
			const fabRoot = document.getElementById("fab-root");
			if (!fabRoot?.shadowRoot) return false;

			const webchat = fabRoot.shadowRoot.querySelector(".bpWebchat");
			if (!webchat) return false;

			const style = window.getComputedStyle(webchat);
			const isOpen = style.visibility === "visible" && style.opacity === "1";

			return isOpen;
		};

		// Set up interval to check state
		const interval = setInterval(() => {
			const newState = checkChatState();
			if (newState !== previousStateRef.current) {
				// Use .current
				previousStateRef.current = newState; // Update .current
				setIsOpen(newState);
			}
		}, 300); // Check every 300ms for responsiveness

		// Also set up MutationObserver for immediate detection
		const observer = new MutationObserver(() => {
			const newState = checkChatState();
			if (newState !== previousStateRef.current) {
				// Use .current
				previousStateRef.current = newState; // Update .current
				setIsOpen(newState);
			}
		});

		// Observe the fab-root element for attribute changes
		const fabRoot = document.getElementById("fab-root");
		if (fabRoot) {
			observer.observe(fabRoot, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ["style", "class"],
			});
		}

		// Send initial greeting when initialized
		const user = getUserData();
		window.botpress?.sendMessage(`Hello${user.name ? `, ${user.name}` : ""}! How can I help?`);

		return () => {
			clearInterval(interval);
			observer.disconnect();
		};
	}, [isInitialized, setIsOpen, getUserData]); // Removed isOpen from dependencies as previousStateRef handles it

	// Sync User Data when it changes
	useEffect(() => {
		if (isInitialized && window.botpress) {
			window.botpress.updateUser(getUserData());
		}
	}, [isInitialized, getUserData]);

	// Inject custom CSS into shadow DOM for mobile styling
	useEffect(() => {
		if (!isInitialized) return;

		const fabRoot = document.getElementById("fab-root");
		if (!fabRoot?.shadowRoot) return;

		// Check if we're on mobile
		const isMobile = window.innerWidth < 640;
		if (!isMobile) return;

		// Check if style already injected
		if (fabRoot.shadowRoot.getElementById("custom-mobile-styles")) return;

		const style = document.createElement("style");
		style.id = "custom-mobile-styles";
		style.textContent = `
			@media (max-width: 640px) {
				.bpWebchat {
					width: 100vw !important;
					height: 100vh !important;
					max-width: 100vw !important;
					max-height: 100vh !important;
					border-radius: 0 !important;
					left: 0 !important;
					right: 0 !important;
					top: 0 !important;
					bottom: 0 !important;
				}
			}
		`;

		fabRoot.shadowRoot.appendChild(style);
	}, [isInitialized]);

	// Lock body scroll when chat is open (only on mobile)
	useEffect(() => {
		const isMobile = window.innerWidth < 640;
		if (isMobile) {
			document.documentElement.style.overflow = isOpen ? "hidden" : "";
		}
		return () => {
			document.documentElement.style.overflow = "";
		};
	}, [isOpen]);

	return (
		<>
			{/* 1. load inject.js */}
			<Script
				id="botpress-inject"
				src="https://cdn.botpress.cloud/webchat/v3.5/inject.js"
				strategy="afterInteractive"
				onLoad={() => setIsFirstScriptInjected(true)}
			/>

			{/* 2. load config script only after inject is loaded */}
			{isFirstScriptInjected && (
				<Script
					id="botpress-config"
					src="https://files.bpcontent.cloud/2026/01/16/13/20260116130025-JTV8XU38.js"
					strategy="afterInteractive"
					onLoad={() => {
						setIsSecondScriptLoaded(true);
					}}
				/>
			)}
		</>
	);
}
